import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  PortfolioFiscalBucketDTO,
  PortfolioFiscalBucketId,
  PortfolioFiscalBucketKind,
  PortfolioFiscalQuarter,
  PortfolioFiscalReportDTO,
  PortfolioFiscalTransactionDTO,
} from '@inversor-hub/types';
import { useGetMe } from '@/lib/api/me';
import { useGetPortfolioFiscalReport } from '@/lib/api/investment-transactions';
import { formatCurrency } from '@/utils/misc';
import { exportPortfolioFiscalReport } from '@/features/investments/portfolio/exportPortfolioFiscalReport';

const ALL_INVESTMENTS = 'all';
const ALL_YEAR = 'all';

const QUARTER_OPTIONS: PortfolioFiscalQuarter[] = [1, 2, 3, 4];

const DISCLAIMER_BODY =
  'Organiza tus ingresos y gastos en las categorías que usa tu gestor para el IRPF. Faltan partidas que solo tu asesor puede calcular correctamente (como la amortización o la reducción por vivienda habitual). Úsalo para ahorrar tiempo en la declaración, no para sustituirla.';

const KIND_SECTIONS: { kind: PortfolioFiscalBucketKind; title: string }[] = [
  { kind: 'income', title: 'Ingresos' },
  { kind: 'deductible', title: 'Gastos deducibles' },
  { kind: 'non-deductible', title: 'No deducible en IRPF' },
  { kind: 'separate', title: 'Revisar fuera del alquiler' },
  { kind: 'uncategorized', title: 'Sin categoría' },
];

const formatTxDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

export default function PortfolioFiscalReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user, isLoading: isUserLoading } = useGetMe();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [selectedQuarter, setSelectedQuarter] = useState<PortfolioFiscalQuarter | undefined>();
  const [disclaimerOpen, setDisclaimerOpen] = useState(true);
  const {
    data: report,
    isLoading,
    isError,
    isPlaceholderData,
  } = useGetPortfolioFiscalReport(selectedYear, selectedQuarter);

  const selectedInvestmentId = searchParams.get('investmentId') ?? ALL_INVESTMENTS;

  useEffect(() => {
    if (report && !isPlaceholderData && selectedYear === undefined) setSelectedYear(report.year);
  }, [isPlaceholderData, report, selectedYear]);

  const selectedInvestment = useMemo(
    () =>
      selectedInvestmentId === ALL_INVESTMENTS
        ? undefined
        : report?.investments.find(
            (investment) => investment.investmentId === selectedInvestmentId
          ),
    [report, selectedInvestmentId]
  );

  useEffect(() => {
    if (
      report &&
      selectedInvestmentId !== ALL_INVESTMENTS &&
      !report.investments.some((investment) => investment.investmentId === selectedInvestmentId)
    ) {
      setSearchParams({}, { replace: true });
    }
  }, [report, selectedInvestmentId, setSearchParams]);

  const activeTotals = selectedInvestment?.totals ?? report?.totals;
  const activeBuckets = selectedInvestment?.buckets ?? report?.buckets ?? [];
  const activeTransactions = useMemo<PortfolioFiscalTransactionDTO[]>(
    () =>
      selectedInvestment
        ? selectedInvestment.transactions
        : (report?.investments.flatMap((investment) => investment.transactions) ?? []),
    [report, selectedInvestment]
  );

  const investmentNameById = useMemo(
    () =>
      new Map(
        report?.investments.map((investment) => [investment.investmentId, investment.name]) ?? []
      ),
    [report]
  );

  const transactionsByBucket = useMemo(() => {
    const grouped = new Map<PortfolioFiscalBucketId, PortfolioFiscalTransactionDTO[]>();
    for (const transaction of activeTransactions) {
      const list = grouped.get(transaction.bucketId) ?? [];
      list.push(transaction);
      grouped.set(transaction.bucketId, list);
    }
    return grouped;
  }, [activeTransactions]);

  const exportReport = useMemo<PortfolioFiscalReportDTO | undefined>(() => {
    if (!report) return undefined;
    if (!selectedInvestment) return report;
    return {
      ...report,
      totals: selectedInvestment.totals,
      buckets: selectedInvestment.buckets,
      investments: [selectedInvestment],
    };
  }, [report, selectedInvestment]);

  const periodLabel = report
    ? report.quarter
      ? `T${report.quarter} ${report.year}`
      : `${report.year}`
    : '';

  const handleInvestmentChange = (investmentId: string) => {
    if (investmentId === ALL_INVESTMENTS) {
      setSearchParams({});
      return;
    }
    setSearchParams({ investmentId });
  };

  if (isUserLoading) return <Skeleton variant="rectangular" height={320} />;
  if (user?.role !== 'investor') return <Navigate to="/inversiones" replace />;

  const renderBucket = (bucket: PortfolioFiscalBucketDTO) => {
    if (bucket.transactionCount === 0) return null;
    const transactions = (transactionsByBucket.get(bucket.id) ?? [])
      .slice()
      .sort((left, right) => (left.date < right.date ? 1 : -1));
    const isUncategorized = bucket.kind === 'uncategorized';

    return (
      <Accordion key={bucket.id} disableGutters sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1.5 }}>
            {isUncategorized && <WarningAmberIcon color="warning" fontSize="small" />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {bucket.label}
                {bucket.tooltip && (
                  <Tooltip title={bucket.tooltip} arrow>
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }}
                    />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bucket.subtitle}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {formatCurrency(bucket.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bucket.transactionCount}{' '}
                {bucket.transactionCount === 1 ? 'transacción' : 'transacciones'}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {transactions.map((transaction, index) => (
              <ListItem
                key={`${transaction.id}-${transaction.category}-${index}`}
                disableGutters
                divider
              >
                <ListItemText
                  primary={transaction.concept || 'Sin concepto'}
                  secondary={
                    selectedInvestment
                      ? formatTxDate(transaction.date)
                      : `${formatTxDate(transaction.date)} · ${investmentNameById.get(transaction.investmentId) ?? 'Inmueble'}`
                  }
                />
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrency(transaction.amount)}
                </Typography>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSection = (kind: PortfolioFiscalBucketKind, title: string) => {
    const buckets = activeBuckets.filter(
      (bucket) => bucket.kind === kind && bucket.transactionCount > 0
    );
    if (buckets.length === 0) return null;
    return (
      <Box key={kind} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        {buckets.map(renderBucket)}
      </Box>
    );
  };

  const visibleSections = KIND_SECTIONS.filter(({ kind }) =>
    activeBuckets.some((bucket) => bucket.kind === kind && bucket.transactionCount > 0)
  );

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth={1200} mx="auto">
      <Stack spacing={2.5} mb={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <IconButton
              aria-label="Volver al dashboard"
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 0.25 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Resumen fiscal de la cartera
              </Typography>
              <Typography color="text.secondary">
                Agrupa ingresos y gastos por categoría de IRPF de toda tu cartera en España.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!exportReport || exportReport.investments.length === 0}
            onClick={() => exportReport && exportPortfolioFiscalReport(exportReport)}
            sx={{ whiteSpace: 'nowrap', minHeight: 40, flexShrink: 0 }}
          >
            Exportar XLSX
          </Button>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          sx={{ pl: { xs: 0, md: 6.25 } }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
            <InputLabel id="fiscal-investment-label">Vista</InputLabel>
            <Select
              labelId="fiscal-investment-label"
              label="Vista"
              value={selectedInvestmentId}
              onChange={(event) => handleInvestmentChange(event.target.value)}
            >
              <MenuItem value={ALL_INVESTMENTS}>Toda la cartera</MenuItem>
              {report?.investments.map((investment) => (
                <MenuItem key={investment.investmentId} value={investment.investmentId}>
                  {investment.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="fiscal-year-label">Ejercicio</InputLabel>
            <Select
              labelId="fiscal-year-label"
              label="Ejercicio"
              value={selectedYear ?? report?.year ?? ''}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {(report?.availableYears.length ? report.availableYears : [report?.year])
                .filter((year): year is number => year !== undefined)
                .map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel id="fiscal-quarter-label">Trimestre</InputLabel>
            <Select
              labelId="fiscal-quarter-label"
              label="Trimestre"
              value={selectedQuarter ?? ALL_YEAR}
              onChange={(event) =>
                setSelectedQuarter(
                  event.target.value === ALL_YEAR
                    ? undefined
                    : (Number(event.target.value) as PortfolioFiscalQuarter)
                )
              }
            >
              <MenuItem value={ALL_YEAR}>Todo el año</MenuItem>
              {QUARTER_OPTIONS.map((quarter) => (
                <MenuItem key={quarter} value={quarter}>
                  T{quarter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {disclaimerOpen && (
        <Alert severity="info" onClose={() => setDisclaimerOpen(false)} sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Este resumen no es un cálculo fiscal.</AlertTitle>
          {DISCLAIMER_BODY}
        </Alert>
      )}

      {isError && (
        <Alert severity="error">
          No se ha podido generar el resumen fiscal. Inténtalo de nuevo en unos minutos.
        </Alert>
      )}

      {isLoading || !report || !activeTotals ? (
        <Grid container spacing={2}>
          {Array.from({ length: 3 }, (_, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Skeleton variant="rectangular" height={130} />
            </Grid>
          ))}
        </Grid>
      ) : report.investments.length === 0 ? (
        <Alert severity="info">
          Registra al menos un inmueble en España con transacciones para generar el resumen fiscal.
        </Alert>
      ) : activeTotals.transactionCount === 0 ? (
        <Alert severity="info">
          No hay transacciones registradas para {periodLabel}. Añade ingresos y gastos en la pestaña
          Transacciones de cada inmueble para generar el resumen fiscal.
        </Alert>
      ) : (
        <>
          {activeTotals.uncategorizedCount > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
              Hay {activeTotals.uncategorizedCount}{' '}
              {activeTotals.uncategorizedCount === 1 ? 'movimiento' : 'movimientos'} sin categoría.
              Clasifícalos en cada inmueble para que el desglose por categoría sea completo.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Ingresos íntegros
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    {formatCurrency(activeTotals.grossIncome)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingDownIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Gastos deducibles
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="error.main">
                    {formatCurrency(activeTotals.deductibleExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AccountBalanceWalletIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Saldo organizativo
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatCurrency(activeTotals.organizationalBalance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No es el rendimiento neto fiscal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {visibleSections.map(({ kind, title }, index) => (
            <Box key={kind}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              {renderSection(kind, title)}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
